import type { SuggestionResource } from '@/kinds/suggestion'

/**
 * The merge queue as a card shows it (internal/assistant/mergequeue.go has the
 * server side). The state is written on the card (`status.merge`) and syncs
 * like anything else; a position is never stored, it is counted here from the
 * cards of the same repo, so every device agrees without a write per move.
 */

export type MergeView =
  | { state: 'idle' }
  | { state: 'queued'; position: number }
  | { state: 'merging' }
  | { state: 'updating'; reason: string }
  | { state: 'failed'; reason: string }
  | { state: 'merged' }

/** owner/name from whatever the op carries (a URL, a trailing .git). */
export const repoKey = (repo: string): string =>
  repo
    .replace(/\.git$/, '')
    .split('/')
    .slice(-2)
    .join('/')

/** The pull request a card only merges, or null for any other card. */
export const mergeTarget = (s: SuggestionResource): { repo: string; number: number } | null => {
  if (s.spec.ops.length === 0) return null
  let found: { repo: string; number: number } | null = null
  for (const op of s.spec.ops) {
    if (op.type !== 'merge_pr') return null
    const a = (op.args ?? {}) as { repo?: unknown; number?: unknown }
    if (typeof a.repo !== 'string' || typeof a.number !== 'number' || a.number <= 0) return null
    const here = { repo: repoKey(a.repo), number: a.number }
    if (found && (found.repo !== here.repo || found.number !== here.number)) return null
    found = here
  }
  return found
}

export const isMergeCard = (s: SuggestionResource): boolean => mergeTarget(s) !== null

/**
 * Every pull request a card merges when it does nothing but merge, in its order
 * and once each (Prime's "merge everything" carries several), else none.
 */
export const mergeTargets = (s: SuggestionResource): { repo: string; number: number }[] => {
  const out: { repo: string; number: number }[] = []
  for (const op of s.spec.ops) {
    if (op.type !== 'merge_pr') return []
    const a = (op.args ?? {}) as { repo?: unknown; number?: unknown }
    if (typeof a.repo !== 'string' || typeof a.number !== 'number' || a.number <= 0) return []
    const here = { repo: repoKey(a.repo), number: a.number }
    if (!out.some((t) => t.repo === here.repo && t.number === here.number)) out.push(here)
  }
  return out
}

/** Tap order: merging first (it is under way), then who tapped first. */
const inLine = (a: SuggestionResource, b: SuggestionResource): number => {
  const am = a.status.merge?.state === 'merging'
  const bm = b.status.merge?.state === 'merging'
  if (am !== bm) return am ? -1 : 1
  const at = a.status.merge?.queued_at ?? ''
  const bt = b.status.merge?.queued_at ?? ''
  if (at !== bt) return at < bt ? -1 : 1
  return a.id < b.id ? -1 : 1
}

const active = (s: SuggestionResource): boolean =>
  !s.deleted_at &&
  s.status.state === 'pending' &&
  (s.status.merge?.state === 'queued' ||
    s.status.merge?.state === 'merging' ||
    s.status.merge?.state === 'updating')

/** Another card for the same pull request that is in the queue: one merge serves them all. */
const liveSibling = (s: SuggestionResource, cards: SuggestionResource[]): SuggestionResource | undefined => {
  const t = mergeTarget(s)
  if (!t) return undefined
  return cards
    .filter((c) => c.id !== s.id && active(c))
    .filter((c) => {
      const u = mergeTarget(c)
      return u?.repo === t.repo && u.number === t.number
    })
    .sort(inLine)[0]
}

/** Where a card stands. `cards` are the other suggestions the device has (any state). */
export const mergeView = (s: SuggestionResource, cards: SuggestionResource[]): MergeView => {
  if (s.status.state === 'accepted' && isMergeCard(s)) return { state: 'merged' }
  if (s.status.state !== 'pending') return { state: 'idle' }
  const m = s.status.merge
  if (!m || m.state === 'failed') {
    // a second card for a pull request already in the queue shows that card's
    // state, and never queues it twice
    const other = liveSibling(s, cards)
    if (other) return mergeView(other, cards)
  }
  if (!m) return { state: 'idle' }
  if (m.state === 'failed') return { state: 'failed', reason: m.reason || 'The merge did not go through' }
  if (m.state === 'merging') return { state: 'merging' }
  if (m.state === 'updating')
    return {
      state: 'updating',
      reason: m.reason || 'It conflicts with main: its agent is merging main into it',
    }
  const repo = mergeTarget(s)?.repo
  const line = cards
    .filter((c) => active(c) && mergeTarget(c)?.repo === repo)
    .sort(inLine)
    .filter((c) => c.status.merge?.state === 'queued')
  const i = line.findIndex((c) => c.id === s.id)
  return { state: 'queued', position: i < 0 ? 1 : i + 1 }
}

/** The view of a pull request, from the card (if any) that is queuing it: for the review page. */
export const mergeViewFor = (repo: string, number: number, cards: SuggestionResource[]): MergeView => {
  const key = repoKey(repo)
  const mine = cards.filter((c) => {
    const t = mergeTarget(c)
    return !c.deleted_at && t?.repo === key && t.number === number
  })
  const live = mine.filter(active).sort(inLine)[0]
  if (live) return mergeView(live, cards)
  const failed = mine
    .filter((c) => c.status.state === 'pending' && c.status.merge?.state === 'failed')
    .sort((a, b) => ((a.status.merge?.failed_at ?? '') < (b.status.merge?.failed_at ?? '') ? 1 : -1))[0]
  if (failed) return mergeView(failed, cards)
  return { state: 'idle' }
}

/** One plain line for a view, or '' when there is nothing to say. */
export const mergeWords = (v: MergeView): string => {
  switch (v.state) {
    case 'queued':
      return `Queued (${v.position})`
    case 'merging':
      return 'Merging…'
    case 'merged':
      return 'Merged'
    case 'updating':
      return v.reason
    case 'failed':
      return v.reason
    default:
      return ''
  }
}

/** 42s, 1m 32s, 12m: how long a merge has run, or took. */
export const formatElapsed = (ms: number): string => {
  const total = Math.max(0, Math.round(ms / 1000))
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  const sec = total % 60
  return sec ? `${m}m ${sec}s` : `${m}m`
}

/** The merging line with the time it has run, from the card's `started_at`. */
export const mergingWords = (startedAt: string | undefined, now: number): string => {
  const t = startedAt ? Date.parse(startedAt) : NaN
  return Number.isNaN(t) ? 'Merging…' : `Merging… ${formatElapsed(now - t)}`
}

/** What a merged card says: Merged, with how long Forgejo took when it was slow. */
export const mergedWords = (s: SuggestionResource): string => {
  const ms = s.status.merge?.took_ms ?? 0
  return ms >= 10_000 ? `Merged in ${formatElapsed(ms)}` : 'Merged'
}

/** When the merge this card is waiting on began: its own, or the sibling card's that carries it. */
export const mergeStartedAt = (s: SuggestionResource, cards: SuggestionResource[]): string | undefined =>
  s.status.merge?.state === 'merging'
    ? s.status.merge.started_at
    : liveSibling(s, cards)?.status.merge?.started_at
